import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo-workspace.png';
import {
  checkToolAccess,
  loginToolUser,
  type ToolAccessResult,
  type ToolService,
} from '../../lib/toolAccess';

const labels: Record<ToolService, string> = {
  simulator: 'محاكي الدوائر الكهربائية',
  plc: 'محاكي PLC',
  faults: 'فحص أعطال الكهرباء',
  lighting: 'حساب الإضاءة المناسبة',
};

const WHATSAPP_NUMBER = '9647779577796';
const WHATSAPP_TEXT = encodeURIComponent(
  'السلام عليكم، أريد تفعيل الاشتراك الكامل للموقع. الاشتراك الشهري 5000 دينار عراقي (3 دولار)، والاشتراك السنوي 50000 دينار عراقي (33 دولار).',
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`;

export default function ToolAccessGate({ service, children }: { service: ToolService; children: ReactNode }) {
  const [result, setResult] = useState<ToolAccessResult | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [fieldsUnlocked, setFieldsUnlocked] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void checkToolAccess(service).then((next) => {
      if (mounted.current) setResult(next);
    });
    return () => {
      mounted.current = false;
    };
  }, [service]);

  // إبقاء حقول الدخول فارغة ومنع المتصفح من تعبئتها تلقائياً بحساب صاحب الجهاز.
  useEffect(() => {
    setUsername('');
    setPassword('');
    const timer = window.setTimeout(() => {
      setUsername('');
      setPassword('');
      setFieldsUnlocked(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, []);

  async function login(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password) return;
    setLoggingIn(true);
    setMessage('');
    const response = await loginToolUser(username, password);
    if (response.ok) {
      setResult({
        allowed: true,
        mode: 'subscriber',
        remaining: null,
        limit: null,
        username: response.username,
        expiresAt: response.expiresAt,
        systemReady: true,
      });
    } else {
      setMessage(response.message);
    }
    setLoggingIn(false);
  }

  if (!result) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#171717] text-white" dir="rtl">
        <div className="h-10 w-10 rounded-full border-[3px] border-white/15 border-t-[#F5A623] animate-spin" />
      </div>
    );
  }

  if (result.allowed) return <>{children}</>;

  return (
    <div className="min-h-screen bg-[#171717] text-white flex items-center justify-center p-4 sm:p-6" dir="rtl">
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] p-5 sm:p-8 shadow-2xl">
        {/* القفل صار علامة صغيرة بأعلى اليمين بدلاً من الأيقونة الكبيرة في الوسط */}
        <div className="absolute right-4 top-4 h-10 w-10 rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/10 text-[#F5A623] grid place-items-center text-xl">
          <i className="ri-lock-2-line" />
        </div>

        {/* شعار المهندس مكان أيقونة القفل القديمة */}
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-3xl border border-white/10 bg-white/[0.04] p-2 shadow-lg sm:h-28 sm:w-28">
          <img src={logo} alt="المهندس" className="h-full w-full object-contain" draggable={false} />
        </div>

        <h1 className="mt-5 text-center text-2xl font-bold">انتهت التجربة المجانية</h1>
        <p className="mt-2 text-center text-sm leading-7 text-gray-400">
          اكتمل عدد مرات الاستخدام المجاني لخدمة{' '}
          <span className="font-bold text-[#F5A623]">{labels[service]}</span>.
        </p>

        <div className="mt-5 rounded-2xl border border-[#F5A623]/25 bg-[#F5A623]/[0.08] p-4 sm:p-5">
          <p className="text-center text-base font-bold text-white">اختر مدة الاشتراك الكامل</p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center">
              <p className="text-sm font-bold text-gray-200">اشتراك شهري</p>
              <p className="mt-2 text-xl font-black text-[#F5A623]">5,000 دينار عراقي</p>
              <p className="mt-1 text-sm text-gray-300">للشهر الواحد</p>
              <div className="my-3 h-px bg-white/10" />
              <p className="text-lg font-bold text-white">3 دولار</p>
              <p className="mt-1 text-xs text-gray-400">للشهر الواحد</p>
            </div>

            <div className="rounded-2xl border border-[#F5A623]/35 bg-[#F5A623]/[0.06] p-4 text-center">
              <p className="text-sm font-bold text-gray-200">اشتراك سنوي</p>
              <p className="mt-2 text-xl font-black text-[#F5A623]">50,000 دينار عراقي</p>
              <p className="mt-1 text-sm text-gray-300">للسنة الواحدة</p>
              <div className="my-3 h-px bg-white/10" />
              <p className="text-lg font-bold text-white">33 دولار</p>
              <p className="mt-1 text-xs text-gray-400">للسنة الواحدة</p>
            </div>
          </div>

          <p className="mt-4 text-center text-sm leading-7 text-gray-300">
            يشمل <b>محاكي الدوائر الكهربائية</b>، <b>محاكي PLC</b>، <b>فحص الأعطال</b> و<b>حساب الإضاءة</b>.
          </p>
          <p className="mt-2 text-center text-sm text-gray-400">لتفعيل الاشتراك يرجى التواصل عبر الواتساب.</p>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 font-bold text-white transition hover:brightness-110"
          >
            <i className="ri-whatsapp-line text-xl" />
            تفعيل الاشتراك عبر واتساب
          </a>
        </div>

        <div className="my-5 flex items-center gap-3 text-xs text-gray-500">
          <span className="h-px flex-1 bg-white/10" />
          <span>للمشتركين الحاليين</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={login} className="space-y-4" autoComplete="off">
          {/* حقول وهمية تمتص أي AutoFill قديم من المتصفح ولا تظهر للمستخدم */}
          <input
            type="text"
            name="fake-access-user"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            className="pointer-events-none absolute h-px w-px opacity-0"
          />
          <input
            type="password"
            name="fake-access-password"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            className="pointer-events-none absolute h-px w-px opacity-0"
          />

          <div>
            <label className="mb-2 block text-sm text-gray-300">اسم المستخدم</label>
            <input
              name="tool-access-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setFieldsUnlocked(true)}
              autoComplete="off"
              readOnly={!fieldsUnlocked}
              className="admin-input"
              dir="ltr"
              required
            />
          </div>
          <div>
            <label className="mb-2 block text-sm text-gray-300">كلمة المرور</label>
            <input
              type="password"
              name="tool-access-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFieldsUnlocked(true)}
              autoComplete="new-password"
              readOnly={!fieldsUnlocked}
              className="admin-input"
              dir="ltr"
              required
            />
          </div>
          {message && <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-300">{message}</p>}
          <button
            type="submit"
            disabled={loggingIn}
            className="w-full rounded-xl bg-[#F5A623] py-3 font-bold text-black transition hover:brightness-110 disabled:opacity-60"
          >
            {loggingIn ? 'جاري التحقق...' : 'دخول'}
          </button>
        </form>

        <Link to="/" className="mt-4 block text-center text-sm text-gray-400 hover:text-white">
          الرجوع للصفحة الرئيسية
        </Link>
      </div>
    </div>
  );
}
